/**
 * Seeding the Form Assignments grid from the schedule (item 5, 2026-10-01).
 *
 * visits[].forms now holds ONLY real CRF form names, put there by the strict
 * matcher (matchScheduleFormsV2). So seeding is a plain lookup by name: exact,
 * or the same name ignoring case and punctuation. No substring and no shared-
 * word guessing; that old fuzzy seed is what let wrong assignments in.
 *
 * Suggest Assignments uses the SAME matcher (the wording rule in
 * functions/src/schedule/visitFormMatch.ts) over the schedule's own labels, so
 * a suggestion can never contradict the schedule. Still only a suggestion: the
 * page leaves it unsaved until the person presses Save.
 *
 * Pure. No React, no Firestore.
 */
import { matchVisitForms } from '../../../../functions/src/schedule/visitFormMatch';

export interface SeedForm { id: string; name: string }
export interface SeedVisit {
  id: string;
  forms?: unknown;
  assessments?: unknown;
  formsToConfirm?: unknown;
  /** How a label was matched (from matchScheduleFormsV2); wording matches are shown as such. */
  formMatches?: unknown;
}

const norm = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();

export function seedAssignmentsFromMatchedVisits(
  visits: SeedVisit[],
  forms: SeedForm[],
): { assignments: Map<string, Set<string>>; ticks: number } {
  const assignments = new Map<string, Set<string>>();
  forms.forEach((f) => assignments.set(f.id, new Set()));
  const byName = new Map<string, string>();
  for (const f of forms) {
    if (!byName.has(f.name)) byName.set(f.name, f.id);
    const k = norm(f.name);
    if (k && !byName.has(`~${k}`)) byName.set(`~${k}`, f.id);
  }
  let ticks = 0;
  for (const v of visits ?? []) {
    for (const ref of Array.isArray(v?.forms) ? (v.forms as unknown[]) : []) {
      if (typeof ref !== 'string' || !ref.trim()) continue;
      const id = byName.get(ref.trim()) ?? byName.get(`~${norm(ref)}`);
      if (id && !assignments.get(id)!.has(v.id)) {
        assignments.get(id)!.add(v.id);
        ticks++;
      }
    }
  }
  return { assignments, ticks };
}

function matchedFormCount(visits: SeedVisit[]): number {
  let n = 0;
  for (const v of visits ?? []) {
    for (const f of Array.isArray(v?.forms) ? (v.forms as unknown[]) : []) if (typeof f === 'string' && f.trim()) n++;
  }
  return n;
}

/**
 * Which copy of the schedule to seed from, after matching has run (docgen
 * crf-list 1b). The page re-reads studyDesign/config right after the match
 * callable wrote it, but a read made while the page's own listener is attached
 * can hand back the listener's older copy, with the matched names missing: the
 * grid then opened with 0 ticks per visit although the stored visits held the
 * matches (Suggest Assignments filled them, because it reads the live copy).
 * Take whichever copy carries more matched forms; a tie keeps the fresh read.
 */
export function pickSeedVisits<T extends SeedVisit>(fresh: T[] | null | undefined, live: T[] | null | undefined): T[] {
  const f = Array.isArray(fresh) ? fresh : [];
  const l = Array.isArray(live) ? live : [];
  return matchedFormCount(l) > matchedFormCount(f) ? l : f;
}

/** An all-empty seeded mapping is never saved: a later open can still seed. */
export function shouldAutoSaveSeed(ticks: number): boolean {
  return ticks > 0;
}

const STRICT_HOW = new Set(['exact', 'normalised', 'code', 'alias']);

/** Cells (form id -> visit id -> the schedule's own label) that were matched by wording, so the screen can say so. */
export function wordingMatchedCells(visits: SeedVisit[], forms: SeedForm[]): Map<string, Map<string, string>> {
  const out = new Map<string, Map<string, string>>();
  const idByName = new Map<string, string>(forms.map((f) => [f.name, f.id]));
  for (const v of visits ?? []) {
    for (const m of Array.isArray(v?.formMatches) ? (v.formMatches as Array<Record<string, unknown>>) : []) {
      if (typeof m?.how !== 'string' || STRICT_HOW.has(m.how)) continue;
      const id = typeof m.form === 'string' ? idByName.get(m.form) : undefined;
      if (!id) continue;
      if (!out.has(id)) out.set(id, new Map());
      out.get(id)!.set(v.id, typeof m.label === 'string' ? m.label : '');
    }
  }
  return out;
}

/**
 * Suggest Assignments: the one matcher over every label a visit carries
 * (assessments, labels still to confirm, forms already matched). Nothing is
 * invented: a label that matches no form, or matches two, gets no tick.
 */
export function suggestAssignmentsFromSchedule(
  visits: SeedVisit[],
  forms: SeedForm[],
): { assignments: Map<string, Set<string>>; ticks: number; byWording: Map<string, Map<string, string>> } {
  const assignments = new Map<string, Set<string>>();
  forms.forEach((f) => assignments.set(f.id, new Set()));
  const idByName = new Map<string, string>(forms.map((f) => [f.name, f.id]));
  const byWording = new Map<string, Map<string, string>>();
  let ticks = 0;
  for (const v of visits ?? []) {
    const labels: unknown[] = [];
    for (const key of ['assessments', 'formsToConfirm', 'forms'] as const) {
      const list = (v as unknown as Record<string, unknown>)?.[key];
      if (Array.isArray(list)) labels.push(...list);
    }
    const r = matchVisitForms(labels, forms.map((f) => ({ name: f.name })));
    for (const m of r.matched) {
      const id = idByName.get(m.form);
      if (!id) continue;
      if (!assignments.get(id)!.has(v.id)) {
        assignments.get(id)!.add(v.id);
        ticks++;
      }
      if (m.how !== 'exact' && m.how !== 'normalised' && m.how !== 'code' && m.how !== 'alias') {
        if (!byWording.has(id)) byWording.set(id, new Map());
        byWording.get(id)!.set(v.id, m.label);
      }
    }
  }
  return { assignments, ticks, byWording };
}

/** Ticks across a whole assignments map (form id -> visit ids). */
export function countTicks(assignments: Map<string, Set<string>> | null | undefined): number {
  let n = 0;
  assignments?.forEach((s) => { n += s.size; });
  return n;
}

/**
 * Seeding as a DECISION that is re-asked whenever the schedule or the form list
 * changes, instead of one awaited run that any re-render could cancel (docgen
 * seedfix, 2026-10-02). The old effect awaited the match callable (~10 s) and
 * then dropped its answer if forms.length or visits.length had moved in the
 * meantime, which they do while the page finishes loading; every run was
 * cancelled before it seeded and the grid opened with no ticks.
 *
 * Seeds only when nothing is ticked yet (neither saved nor by the person), the
 * schedule carries matched forms, and this open has not seeded already. An
 * all-empty seed is never written; a saved mapping is never overwritten (an
 * existing but empty one is seeded in memory only, for the person to Save).
 */
export function planSeed(input: {
  mappingExists: boolean;
  savedTicks: number;
  currentTicks: number;
  seededAlready: boolean;
  visits: SeedVisit[];
  forms: SeedForm[];
}): { seed: boolean; assignments: Map<string, Set<string>>; ticks: number; autoSave: boolean } {
  const none = { seed: false, assignments: new Map<string, Set<string>>(), ticks: 0, autoSave: false };
  if (input.seededAlready || input.savedTicks > 0 || input.currentTicks > 0) return none;
  if (!input.forms?.length) return none;
  const s = seedAssignmentsFromMatchedVisits(input.visits, input.forms);
  if (!shouldAutoSaveSeed(s.ticks)) return none;
  return { seed: true, assignments: s.assignments, ticks: s.ticks, autoSave: !input.mappingExists };
}

/** The match callable runs once per project open, not once per effect run. */
export function shouldStartMatch(matchedForProject: string | null, projectId: string): boolean {
  return matchedForProject !== projectId;
}
